import { createContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { ExercisePauseState, PauseCommand } from "@calega/content";
import { api } from "../lib/api";
import { useRealtime } from "../realtime/RealtimeContext";
import { waitForPendingAnswers } from "./answer-delivery";
import { exerciseRunEndedEvent, exerciseRunStartedEvent } from "./learning-api";
import { activateExerciseClock, PauseClock } from "./pause-clock";
import { authenticationClientReference, onAuthenticationClientReference } from "../lib/session-activity";

export const ExercisePauseActionContext = createContext<(() => void) | null>(null);
export const ExercisePauseVisibilityContext = createContext(false);
export const ExercisePauseButtonSlotContext = createContext<((element: HTMLSpanElement | null) => void) | null>(null);

let audio: AudioContext | null = null;
function prepareAudio() {
  try { audio ??= new AudioContext(); void audio.resume().catch(() => undefined); } catch { /* The visual reminder remains available. */ }
}
function signal() {
  if (!audio || audio.state !== "running") return;
  for (const [offset, frequency] of [[0, 660], [.3, 880]] as const) {
    const tone = audio.createOscillator(); const gain = audio.createGain();
    const start = audio.currentTime + offset;
    tone.frequency.value = frequency;
    gain.gain.setValueAtTime(0, start); gain.gain.linearRampToValueAtTime(.18, start + .025);
    gain.gain.exponentialRampToValueAtTime(.001, start + .25);
    tone.connect(gain).connect(audio.destination); tone.start(start); tone.stop(start + .27);
    tone.onended = () => { tone.disconnect(); gain.disconnect(); };
  }
}

export function ExercisePauseBoundary({ exerciseType, itemRunId, itemActive, preparePause, children }: { exerciseType: string; itemRunId: string; itemActive: boolean; preparePause?: () => Promise<string>; children: ReactNode }) {
  const realtime = useRealtime();
  const realtimeRef = useRef(realtime); realtimeRef.current = realtime;
  const previousSessionId = useRef(realtime.session?.id);
  const authenticationReference = useRef(authenticationClientReference());
  const clock = useRef<PauseClock>(new PauseClock(() => realtimeRef.current.serverNow()));
  const [runId, setRunId] = useState("");
  const runIdRef = useRef("");
  const pauseServerOffset = useRef<number | null>(null);
  const [state, setState] = useState<ExercisePauseState | null>(null);
  const stateRef = useRef(state); stateRef.current = state;
  const [opening, setOpening] = useState(false);
  const openingPending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [, tick] = useState(0);
  const [phase, setPhase] = useState("");
  const [buttonSlot, setButtonSlot] = useState<HTMLSpanElement | null>(null);
  const dialog = useRef<HTMLElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const signaled = useRef<number | null>(null);
  const active = Boolean(itemActive && (runId || preparePause) && (!state || state.status === "STARTED"));
  const synchronizing = Boolean(realtime.session && itemRunId && itemActive && (!state || state.runId !== itemRunId));
  const paused = Boolean(itemActive && (opening || state?.startedAt || synchronizing));
  const pauseNow = () => realtimeRef.current.serverNow() + (pauseServerOffset.current ?? 0);
  const expired = Boolean(state?.until && pauseNow() >= state.until);
  const path = `/learning/runs/${encodeURIComponent(runId)}/pause`;
  const query = realtime.session ? `?sessionId=${encodeURIComponent(realtime.session.id)}` : "";

  function adoptRun(id: string) {
    if (id !== runIdRef.current) {
      clock.current.clear(); stateRef.current = null; pauseServerOffset.current = null; signaled.current = null;
      setState(null); setError("");
    }
    runIdRef.current = id;
    setRunId(id);
  }

  function accept(next: ExercisePauseState) {
    if (next.runId !== runIdRef.current) return;
    const previous = stateRef.current;
    if (previous?.runId === next.runId && (previous.revision > next.revision || (previous.reflection?.revision ?? 0) > (next.reflection?.revision ?? 0))) return;
    if (previous?.until && next.serverNow >= previous.until && signaled.current !== previous.until) { signal(); signaled.current = previous.until; }
    stateRef.current = next;
    pauseServerOffset.current ??= next.serverNow - realtimeRef.current.serverNow();
    clock.current.synchronize(next.pausedMs, (next.clockStoppedAt ?? next.startedAt) === null ? null : (next.clockStoppedAt ?? next.startedAt)! - pauseServerOffset.current);
    realtimeRef.current.pauseExerciseDelivery(next.runId, Boolean(next.startedAt && next.status === "STARTED"));
    setState(next);
    if (!openingPending.current) setOpening(false);
    if (next.status !== "STARTED") window.dispatchEvent(new CustomEvent(exerciseRunEndedEvent, { detail: { runId: next.runId, abortReason: next.abortReason ?? null } }));
  }

  useLayoutEffect(() => {
    activateExerciseClock(clock.current);
    return () => { clock.current.clear(); activateExerciseClock(null); };
  }, []);
  useEffect(() => {
    const preparingReflection = (event: Event) => {
      const detail = (event as CustomEvent<{ runId: string; active: boolean }>).detail;
      if (detail.runId !== runIdRef.current) return;
      const current = stateRef.current;
      const stoppedAt = current?.clockStoppedAt ?? current?.startedAt ?? (detail.active ? realtimeRef.current.serverNow() + (pauseServerOffset.current ?? 0) : null);
      clock.current.synchronize(current?.pausedMs ?? 0, stoppedAt === null ? null : stoppedAt - (pauseServerOffset.current ?? 0));
    };
    window.addEventListener("calepia:reflection-preparing", preparingReflection);
    return () => window.removeEventListener("calepia:reflection-preparing", preparingReflection);
  }, []);
  useEffect(() => {
    authenticationReference.current ||= authenticationClientReference();
    return onAuthenticationClientReference((id) => { if (!authenticationReference.current) authenticationReference.current = id; });
  }, []);
  function pauseHeaders() {
    if (!authenticationReference.current) throw new Error("Deine Anmeldung wird noch geprüft. Bitte versuche es gleich erneut.");
    return { "X-Pause-Client-Id": authenticationReference.current };
  }
  useLayoutEffect(() => {
    if (synchronizing) clock.current.synchronize(stateRef.current?.pausedMs ?? 0, realtime.serverNow());
  }, [synchronizing]);
  useEffect(() => {
    if (previousSessionId.current && !realtime.session) {
      runIdRef.current = "";
      setRunId(""); setState(null); stateRef.current = null; pauseServerOffset.current = null; setOpening(false); clock.current.clear();
    }
    previousSessionId.current = realtime.session?.id;
  }, [realtime.session?.id]);
  useLayoutEffect(() => {
    if (itemRunId && itemActive) adoptRun(itemRunId);
    else if (!itemActive) { runIdRef.current = ""; setRunId(""); }
  }, [itemRunId, itemActive]);
  useEffect(() => {
    const started = (event: Event) => {
      const id = (event as CustomEvent<{ runId?: string }>).detail?.runId;
      if (!id) return;
      adoptRun(id);
    };
    const ended = (event: Event) => {
      if ((event as CustomEvent<{ runId?: string }>).detail?.runId !== stateRef.current?.runId) return;
      runIdRef.current = "";
      setRunId(""); setOpening(false);
      const closed = { ...stateRef.current!, status: stateRef.current!.status === "STARTED" ? "COMPLETED" : stateRef.current!.status, startedAt: null, until: null };
      setState(closed); stateRef.current = closed; clock.current.clear();
    };
    window.addEventListener(exerciseRunStartedEvent, started);
    window.addEventListener(exerciseRunEndedEvent, ended);
    document.addEventListener("pointerdown", prepareAudio, { once: true });
    return () => { window.removeEventListener(exerciseRunStartedEvent, started); window.removeEventListener(exerciseRunEndedEvent, ended); document.removeEventListener("pointerdown", prepareAudio); };
  }, []);
  useLayoutEffect(() => {
    const envelope = realtime.state.exercise as { exerciseType?: string; runId?: string; state?: { runId?: string; phase?: string; submitted?: boolean } | null } | null;
    if (envelope?.exerciseType !== exerciseType || !envelope.state) { if (realtime.session) { runIdRef.current = ""; setRunId(""); } return; }
    setPhase(envelope.state.phase ?? "");
    if (envelope.state.phase === "complete" || envelope.state.phase === "review" || envelope.state.submitted) { runIdRef.current = ""; setRunId(""); setOpening(false); return; }
    const id = envelope.runId ?? envelope.state.runId;
    if (id) adoptRun(id);
  }, [realtime.state.exercise, realtime.session?.id, exerciseType]);
  useEffect(() => {
    const localPhase = (event: Event) => setPhase((event as CustomEvent<string>).detail);
    window.addEventListener("calepia:blitz-phase", localPhase);
    return () => window.removeEventListener("calepia:blitz-phase", localPhase);
  }, []);

  useEffect(() => {
    if (!runId) return;
    let cancelled = false; let pending = false;
    const check = async () => {
      if (pending) return; pending = true;
      try {
        const next = await api<ExercisePauseState>(path + query, { signal: AbortSignal.timeout(5_000) });
        if (!cancelled) accept(next);
      } catch (cause) { if (!cancelled && stateRef.current?.startedAt) setError(cause instanceof Error ? cause.message : "Die Verbindung konnte nicht geprüft werden."); }
      finally { pending = false; }
    };
    const update = (event: Event) => {
      const next = (event as CustomEvent<ExercisePauseState>).detail;
      if (!cancelled && next.runId === runId) accept(next);
    };
    void check();
    const timer = window.setInterval(() => {
      tick((value) => value + 1);
      const current = stateRef.current;
      if (current?.until && pauseNow() >= current.until) {
        if (signaled.current !== current.until) { signal(); signaled.current = current.until; }
        if (current.resumeMode === "AUTO") void check();
      }
    }, 500);
    const polling = window.setInterval(() => void check(), 10_000);
    window.addEventListener("calepia:pause-state", update);
    window.addEventListener("focus", check); window.addEventListener("online", check);
    return () => { cancelled = true; clearInterval(timer); clearInterval(polling); window.removeEventListener("calepia:pause-state", update); window.removeEventListener("focus", check); window.removeEventListener("online", check); };
  }, [runId, path, query, realtime.connected, realtime.synchronized]);

  useEffect(() => {
    if (!state?.startedAt || !runId) return;
    let cancelled = false;
    const confirm = async () => {
      try {
        const next = await api<ExercisePauseState>(path.replace(/\/pause$/, "/pause/presence") + query, {
          method: "POST", headers: pauseHeaders(), body: JSON.stringify({ revision: stateRef.current?.revision }), signal: AbortSignal.timeout(5_000),
        });
        if (!cancelled) { accept(next); setError(""); }
      } catch (cause) { if (!cancelled) setError(cause instanceof Error ? cause.message : "Die Verbindung konnte nicht bestätigt werden."); }
    };
    void confirm();
    const timer = window.setInterval(() => void confirm(), 30_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [runId, state?.startedAt, state?.revision, path, query]);

  useEffect(() => {
    if (!paused) return;
    const previous = document.activeElement as HTMLElement | null;
    const scroll = document.body.style.overflow; document.body.style.overflow = "hidden";
    content.current?.closest(".content-card")?.classList.add("exercise-page-paused");
    const focusInside = () => {
      if (!dialog.current || dialog.current.contains(document.activeElement)) return;
      const button = dialog.current.querySelector<HTMLButtonElement>("button:not(:disabled)");
      if (button) button.focus(); else dialog.current.focus();
    };
    dialog.current?.focus();
    document.addEventListener("focusin", focusInside);
    window.speechSynthesis?.pause();
    return () => { document.removeEventListener("focusin", focusInside); document.body.style.overflow = scroll; content.current?.closest(".content-card")?.classList.remove("exercise-page-paused"); window.speechSynthesis?.resume(); previous?.focus(); };
  }, [paused]);
  useLayoutEffect(() => {
    if (paused && !busy && dialog.current && (!dialog.current.contains(document.activeElement) || document.activeElement === dialog.current)) {
      dialog.current.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
    }
  }, [paused, busy, state?.chosen]);

  async function command(input: Omit<PauseCommand, "revision"> | { action: "duration"; seconds: 60 | 120 | 300 | null }) {
    if (busy || (!runId && (input.action !== "begin" || !preparePause))) return;
    prepareAudio(); setBusy(true); setError("");
    let target = path + query;
    if (input.action === "begin") { openingPending.current = true; setOpening(true); clock.current.synchronize(stateRef.current?.pausedMs ?? 0, realtime.serverNow()); }
    try {
      const id = runId || await preparePause!();
      target = `/learning/runs/${encodeURIComponent(id)}/pause${query}`;
      if (input.action === "begin") await waitForPendingAnswers(id);
      if (input.action === "begin" && realtimeRef.current.session) {
        const deadline = performance.now() + 12_000;
        while (!realtimeRef.current.synchronized) {
          if (stateRef.current?.startedAt) return;
          if (!realtimeRef.current.connected || performance.now() >= deadline) throw new Error("Die Eingaben wurden noch nicht synchronisiert. Bitte prüfe die Verbindung und versuche die Pause erneut.");
          await new Promise<void>((resolve) => window.setTimeout(resolve, 50));
        }
      }
      const current = stateRef.current ?? await api<ExercisePauseState>(target);
      accept(await api<ExercisePauseState>(target, { method: "POST", headers: pauseHeaders(), body: JSON.stringify({ ...input, revision: current.revision }), signal: AbortSignal.timeout(12_000) }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Die Pause konnte nicht bestätigt werden.");
      try { accept(await api<ExercisePauseState>(target, { signal: AbortSignal.timeout(5_000) })); }
      catch { /* Remain covered until the state can be confirmed. */ }
    } finally {
      openingPending.current = false;
      setBusy(false);
      if (stateRef.current) { setOpening(false); clock.current.synchronize(stateRef.current.pausedMs, (stateRef.current.clockStoppedAt ?? stateRef.current.startedAt) === null ? null : (stateRef.current.clockStoppedAt ?? stateRef.current.startedAt)! - (pauseServerOffset.current ?? 0)); }
    }
  }

  const remaining = state?.until ? Math.max(0, Math.ceil((state.until - pauseNow()) / 1_000)) : null;
  const sessionRemaining = realtime.session ? Math.max(0, Math.ceil((new Date(realtime.session.expiresAt).getTime() - realtime.serverNow()) / 1_000)) : null;
  const pauseButton = active && <div className="exercise-pause-toolbar"><button type="button" className="secondary exercise-pause-button" disabled={busy || paused || (exerciseType === "blitzlesen" && phase === "showing") || Boolean(realtime.session && !realtime.connected)} onClick={() => void command({ action: "begin" })}><svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg><span>Pause</span></button></div>;
  return <ExercisePauseActionContext.Provider value={() => void command({ action: "begin" })}><ExercisePauseVisibilityContext.Provider value={paused}><ExercisePauseButtonSlotContext.Provider value={setButtonSlot}>
    {buttonSlot ? createPortal(pauseButton, buttonSlot) : pauseButton}
    {!paused && error && <p className="alert error" role="status">{error}</p>}
    <div ref={content} className={paused ? "exercise-pause-content hidden" : "exercise-pause-content"} inert={paused} aria-hidden={paused || undefined}>{children}</div>
    {paused && createPortal(<div className="exercise-pause-backdrop">
      <section ref={dialog} tabIndex={-1} className="exercise-pause-modal" role="dialog" aria-modal="true" aria-labelledby="exercise-pause-title" onKeyDown={(event) => {
        if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); }
        if (event.key !== "Tab") return;
        const buttons = Array.from(dialog.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? []);
        if (!buttons.length || document.activeElement === dialog.current || document.activeElement === (event.shiftKey ? buttons[0] : buttons.at(-1))) { event.preventDefault(); (event.shiftKey ? buttons.at(-1) : buttons[0])?.focus(); }
      }}>
        <img src="/brand/lepi-pause.webp" alt="" />
        <h2 id="exercise-pause-title">{expired ? "Deine Pause ist vorbei" : "Zeit für eine Pause"}</h2>
        <p role="status">{synchronizing ? "Deine Aufgabe wird geprüft …" : opening ? "Deine Pause wird bestätigt …" : expired ? "Du kannst jetzt fortsetzen." : "Deine Aufgabe wartet auf dich."}</p>
        {!opening && !synchronizing && !state?.chosen && <div className="exercise-pause-choices">{([60, 120, 300, null] as const).map((seconds) => <button className="secondary" type="button" disabled={busy} key={seconds ?? "unlimited"} onClick={() => void command({ action: "duration", seconds })}>{seconds === null ? "Unbegrenzt" : `${seconds / 60} ${seconds === 60 ? "Minute" : "Minuten"}`}</button>)}</div>}
        {state?.chosen && <p className="exercise-pause-countdown" role="timer" aria-live="off">{remaining === null ? "Ohne Zeitlimit" : `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`}</p>}
        {state?.until && !expired && <p>{state.resumeMode === "AUTO" ? "Nach der Pause geht es automatisch weiter." : "Wenn die Pause vorbei ist, drücke Fortsetzen."}</p>}
        {sessionRemaining !== null && sessionRemaining <= 300 && <p className="session-expiry-warning">Die gemeinsame Sitzung endet in <span role="timer" aria-live="off">{Math.floor(sessionRemaining / 60)}:{String(sessionRemaining % 60).padStart(2, "0")}</span> Minuten.</p>}
        {error && <p className="alert error" role="alert">{error}</p>}
        <div className="exercise-pause-warning-host" />
        <button className="primary" type="button" disabled={busy || synchronizing} onClick={() => void command({ action: "resume" })}>Fortsetzen</button>
      </section>
    </div>, document.body)}
  </ExercisePauseButtonSlotContext.Provider></ExercisePauseVisibilityContext.Provider></ExercisePauseActionContext.Provider>;
}
